import React, { Component, CSSProperties } from 'react';
import _ from 'lodash';
import clsx from 'clsx';
import CheckBox from 'components/ui/checkBox/CheckBox';
import BlazePagination from 'components/ui/pagination/BlazePagination';
import ExpandChevron from 'components/ui/expandChevron/ExpandChevron';
import Radio from 'components/ui/radio/Radio';
import FullPageLoader from 'components/loaders/FullPageLoader';
import BlazeTableHeader from 'components/blazeTable/BlazeTableHeader';
import { BlazeTableColDef, BlazeTableOptions, SelectAllType } from 'components/blazeTable/BlazeTable.types';
import { Sticky, StickyContainer } from 'react-sticky';
import BlazeTableCell from './BlazeTableCell';
import { SortConfiguration } from '../../types/rest/paginationTypes';
import { sortData } from '../../utils/filterUtils';

type BlazeTableStateProps = {
  expanded: number | null;
  selected: number[];
  selectAll: null | SelectAllType;
  activePage: number;
  pageSize: number;
  sorting?: SortConfiguration;
};

type BlazeTableProps = {
  data: Array<any>;
  options: BlazeTableOptions;

  /**
   * It is possible to create Ref and control the table from parent component.
   */
  onRef?: any;

  /**
   * Selection of items has changed handler. If client side pagination is enabled, this function returns
   * list of selected objects which are passed as data to the table. If backend pagination is enabled, it returns
   * just list of ids.
   * @param selectedItems
   * @param selectAll - tells which type of "selectAll" is triggered
   */
  onSelectionChange?: (selectedItems: Array<any>, selectAll?: SelectAllType) => void;

  /**
   * Page is changed handler.
   * @param pageNumber
   */
  onPageChange?: (pageNumber: number) => void;

  /**
   * Page size is changed handler.
   * @param pageNumber
   */
  onPageSizeChange?: (pageSize: number) => void;

  /** When the table is sorted by column. */
  onSort?: (sortConfig: SortConfiguration) => void;

  /**
   * Item is expanded handler.
   * @param id
   */
  onExpandedItemChange?: (id: number | null) => void;

  /** Override of expanded item. If you need to set expanded item from outside the table.
   Make sure you also provide onExpandedItemChange handler in blazeTable*/
  expandedItem?: number | null;

  /** Override of selected items. */
  selectedItems?: number[] | null;

  /**
   * Boolean indicating data is being loaded. If is set to true, content
   * of the table is blurred.
   */
  isLoading?: boolean;

  /**
   * Custom class for the table object.
   */
  className?: string;

  /**
   * Enables sticky headers for the table (they are fixed on top of the page during the scrolling)
   */
  stickyHeaders?: boolean;

  /**
   * Controls dropdown with number of pages.
   */
  showPageSizeDropdown?: boolean;

  /**
   * Array of available page sizes. If not set, default is 50, 25, 10, 5
   */
  pageSizeOptions?: number[];

  /**
   * Custom resolver for a row key.
   * Each item in the table has to have unique id.
   * Blaze Table uses by default id of the data element. */
  resolveRowKey?: (item: any) => void;

  /**
   * Custom class for pagination position.
   */
  paginationClassName?: string;
};

const resolvePageSize = (pageable: boolean, propsPageSize: number | undefined) => {
  let pageSize: number = pageable ? 10 : 999999;
  if (propsPageSize) {
    pageSize = propsPageSize;
  }
  return pageSize;
};

class BlazeTable extends Component<BlazeTableProps, BlazeTableStateProps> {
  // Column with checkbox needs to be wider when there is a carret with dropdown menu
  selectColumnSize = this.props.options.selectAllDropdown === true ? 60 : 40;
  expandColumnSize = 40;

  state = {
    expanded: null,
    activePage: 1,
    selected: this.props.selectedItems ? this.props.selectedItems : ([] as any),
    selectAll:
      this.props.options.selectAllType === 'VISIBLE' || this.props.options.selectAllType === 'ALL'
        ? this.props.options.selectAllType
        : null,
    tableName: this.props.options.tableName ? this.props.options.tableName : _.uniqueId('tab-'),
    pageSize: resolvePageSize(this.props.options.pageable, this.props.options.pageSize),
    sorting: undefined,
  };

  /**
   * Change active page.
   * @param pageNumber page to display.
   */
  pageSelectedHandler = (pageNumber: number) => {
    // Only change local state when client side pagination is needed
    if (!this._isBackendPaginationEnabled()) {
      this.setState({
        activePage: pageNumber,
      });
    }

    //If there are all items selected on the page, reset the selectAll option
    if (this.state.selectAll === 'VISIBLE') {
      this.setState({ selectAll: null });
    }

    if (this.props.onPageChange) {
      this.props.onPageChange(pageNumber);
    }
  };

  pageSizeSelectedHandler = (pageSize: number) => {
    if (!this._isBackendPaginationEnabled()) {
      this.setState({ pageSize: pageSize });
    }

    if (this.props.onPageSizeChange) {
      this.props.onPageSizeChange(pageSize);
    }
  };

  getSelectType = () => this.props.options.selectType || 'checkbox';

  /**
   * Sort & Slice data for render.
   */
  sortAndSlice = (activePage?: number) => {
    if (!activePage) {
      activePage = this.state.activePage;
    }

    // If sorting is enabled, sort by key
    let data = _.cloneDeep(this.props.data);
    if (!_.isNil(this.state.sorting)) {
      data = sortData(this.props.data, this.state.sorting);
    }

    // If backend pagination is enabled, take page size from outside props if not use local state
    const pageSize = this.props.options.paginationOverride
      ? this.props.options.paginationOverride.pageSize
      : this.state.pageSize;

    if (activePage > 0 && data.length > 0) {
      const start = (activePage - 1) * pageSize;
      const end = activePage * pageSize;
      return data.slice(start, end);
    }
    return [];
  };

  componentDidMount() {
    if (this.props.onRef) {
      this.props.onRef(this);
    }
  }

  componentDidUpdate(prevProps: Readonly<BlazeTableProps>): void {
    // when data changes, we need to make sure we are on a page that actually exists
    if (!_.isEqual(prevProps.data, this.props.data)) {
      const totalPages = Math.ceil(this.props.data?.length / this.state.pageSize);
      if (this.state.activePage > totalPages) {
        this.setState({
          activePage: totalPages || 1,
        });
      }
    }
    // if there are no data anymore uncheck the select all checkbox
    if (this.props.data?.length === 0 && this.state.selectAll) {
      this.setState({
        selectAll: null,
      });
    }

    // Update expanded item from outside
    if (this.props.expandedItem !== undefined && this.state.expanded !== this.props.expandedItem) {
      this.setState({ expanded: this.props.expandedItem });
    }

    if (prevProps.options.selectAllType !== this.props.options.selectAllType) {
      //We need to reset the selection locally
      if (this.props.options.selectAllType === null) {
        this.setState({
          selected: [],
          selectAll: this.props.options.selectAllType,
        });
      }
    }

    // Update selected items from outside
    if (this.props.selectedItems !== undefined && this.state.selected !== this.props.selectedItems) {
      this.setState({
        selected: this.props.selectedItems !== null ? this.props.selectedItems : [],
      });
    }
  }

  /**
   * Expand row by id.
   * @param id
   */
  expandRowHandler = (id: number) => {
    // If expand is controlled from outside
    if (this.props.expandedItem !== undefined) {
      if (this.props.onExpandedItemChange !== undefined) {
        this.props.onExpandedItemChange(id);
      } else {
        throw new Error('Missing handler for expanded item');
      }
    }
    //Expanded state is in local state
    else {
      if (this.state.expanded === null || this.state.expanded !== id) {
        this.setState({ expanded: id });
      } else {
        // Just toggle it
        this.setState({ expanded: null });
      }
    }
  };

  /**
   * Toggles selection of all items in the table.
   */
  selectAll = (selectAllType?: SelectAllType) => {
    this.setState(prevState => {
      // Same option is selected, do not change the selection
      if (selectAllType && prevState.selectAll === selectAllType) {
        return { ...prevState };
      }

      // If not specified, default select type is ALL VISIBLE elements
      const selectType = selectAllType || 'VISIBLE';

      // Determines whether we are doing any bulk selection (either by clicking the select all
      // checkbox or by using predefined select options in dropdown (= specifying selectAllType)
      const isSelecting = prevState.selectAll === null || selectAllType;

      // Select only current data slice?
      const dataSet = selectType === 'VISIBLE' ? this.sortAndSlice() : this.props.data;
      const selectedItems = isSelecting ? dataSet.map((item: any) => item.id) : [];

      if (this.props.onSelectionChange) {
        // If backend pagination is enabled, return list of ids from all data available
        if (this._isBackendPaginationEnabled()) {
          const ids: number[] = this.props.data.map(item => item.id);
          this.props.onSelectionChange(isSelecting ? ids : [], selectAllType);
        } else {
          this.props.onSelectionChange(isSelecting ? dataSet : [], selectAllType);
        }
      }

      return {
        selectAll: isSelecting ? selectType : null,
        selected: selectedItems,
      };
    });
  };

  deSelectAll = () => {
    this.setState(
      {
        selectAll: null,
        selected: [],
      },
      () => {
        // In case we are storing item selection outside, we reset selection also there
        if (this.props.onSelectionChange) {
          this.props.onSelectionChange([]);
        }
      },
    );
  };

  /**
   * Toggles selection of the single item.
   * @param itemId
   */
  itemSelectionHandler = (itemId: any) => {
    if (this.getSelectType() === 'checkbox') {
      this.setState(prevState => {
        const isSelected = prevState.selected.includes(itemId);
        const updatedSelection = isSelected ? _.without(prevState.selected, itemId) : prevState.selected.concat(itemId);
        const isAllInPageSelected = updatedSelection.length === this.sortAndSlice().length;

        // Only check the "SelectAll" check box in case we are in "select" only visible mode
        const selectAllToBeChecked = isAllInPageSelected && prevState.selectAll === 'VISIBLE' ? 'VISIBLE' : null;

        //Selection change handler is available
        if (this.props.onSelectionChange) {
          // Trigger selection change with list of selected objects
          if (!this._isBackendPaginationEnabled()) {
            this.props.onSelectionChange(this.sortAndSlice().filter((item: any) => updatedSelection.includes(item.id)));
          }
          // When backend pagination is on, trigger selection change with list of selected ids
          else {
            this.props.onSelectionChange(updatedSelection);
          }
        }
        return {
          selectAll: selectAllToBeChecked,
          selected: updatedSelection,
        };
      });
    } else if (this.getSelectType() === 'radio') {
      this.setState({
        selected: [itemId],
      });
      this.goToPageWithSelectedItem(itemId);
      if (this.props.onSelectionChange) {
        this.props.onSelectionChange(this.sortAndSlice().filter((item: any) => item.id === itemId));
      }
    }
  };

  /**
   * Sets the current active page so that the selected item is visible
   * @param itemId
   */
  goToPageWithSelectedItem = (itemId: any) => {
    const isCorrectPage = (data: any[]) => data.find((item: any) => item.id === itemId);
    let currPage = this.sortAndSlice(this.state.activePage);
    if (isCorrectPage(currPage)) {
      return;
    }
    const totalPages = Math.ceil(this.props.data?.length / this.state.pageSize);
    for (let i = 1; i <= totalPages; i++) {
      currPage = this.sortAndSlice(i);
      if (isCorrectPage(currPage)) {
        this.setState({
          activePage: i,
        });
      }
    }
  };

  /**
   * Renders row cells for single item.
   * @param item
   */
  renderRowCells = (item: any) =>
    this.props.options.columnDef.map((columnDefinition: BlazeTableColDef, index: number) => (
      <BlazeTableCell
        expandRowHandler={() => this.expandRowHandler(item.id)}
        key={item.accessor + '-cell-' + index}
        item={item}
        columnDefinition={columnDefinition}
        index={index}
      />
    ));

  /**
   * Generates row data id based on table name and adds index of current row to the end of the string.
   */
  generateRowId = (index: number): string => {
    return this.state.tableName + '-row-' + (index + (this.state.activePage - 1) * this.state.pageSize);
  };

  _isBackendSortingEnabled = (): boolean => {
    return !_.isNil(this.props.options.sortingOverride);
  };

  _isBackendPaginationEnabled = (): boolean => {
    return this.props.options.paginationOverride !== undefined;
  };

  _callOnClickWhenEnabled = (id: number) => {
    if (this.props.options.onRowClick !== undefined) {
      this.props.options.onRowClick(id);
    }
  };

  isItemSelected = (item: any) => {
    return this.state.selected.includes(item.id) || this.state.selectAll === 'ALL';
  };

  onKeyDownHandler = (event: any, id: number) => {
    if (event.keyCode === 13 && this.props.options.onRowClick) {
      this.props.options.onRowClick(id);
    }
  };

  buildTableItems = () => {
    return this.sortAndSlice().map((item: any, index: number) => {
      const rowStyleToUse = this.props.options.customRowClassName
        ? _.isFunction(this.props.options.customRowClassName)
          ? `${this.props.options.customRowClassName(item)} flex-table-item`
          : `${this.props.options.customRowClassName} flex-table-item`
        : 'flex-table-item';
      const itemExpanded: boolean = item.id === this.state.expanded && !_.isNil(item.id);

      return (
        <div
          className={itemExpanded ? clsx(rowStyleToUse, 'flex-table-item-expanded') : rowStyleToUse}
          key={this.props.resolveRowKey !== undefined ? this.props.resolveRowKey(item) : item.id}
          id={this.generateRowId(index)}
          tabIndex={this.props.options.onRowClick ? 0 : undefined}
          onKeyDown={this.props.options.onRowClick ? e => this.onKeyDownHandler(e, item.id) : undefined}
        >
          <div
            id={this.state.tableName + '-' + item.id}
            onClick={() => this._callOnClickWhenEnabled(item.id)}
            className="flex-table-item-heading"
            style={{ padding: this.props.options.rowPadding ? this.props.options.rowPadding : 'auto' }}
          >
            <div className="flex-table-row">
              {this.props.options.showRowExpander && (
                <div
                  className={'flex-table-col flex-table-col-overflow-visible flex-table-col-' + this.expandColumnSize}
                >
                  <ExpandChevron
                    isExpanded={itemExpanded}
                    disabled={this.props.options.isRowExpandable ? !this.props.options.isRowExpandable(item) : false}
                    fnCallback={() => this.expandRowHandler(item.id)}
                    rowId={this.generateRowId(index)}
                  />
                </div>
              )}

              {/* Show select box only if table is selectable */}
              {this.props.options.selectable && (
                <div className={'flex-table-col flex-table-col-' + this.selectColumnSize}>
                  {this.getSelectType() === 'checkbox' && (
                    <CheckBox
                      checked={this.isItemSelected(item)}
                      onCheck={() => this.itemSelectionHandler(item.id)}
                      selectAll={false}
                      disabled={this.props.options.disabled || false}
                    />
                  )}
                  {this.getSelectType() === 'radio' && (
                    <Radio
                      checked={this.state.selected.includes(item.id)}
                      value={item.id}
                      fnCallback={() => this.itemSelectionHandler(item.id)}
                      disabled={this.props.options.disabled || false}
                    />
                  )}
                </div>
              )}
              {this.renderRowCells(item)}
            </div>
          </div>

          {itemExpanded && (
            <div className="flex-table-item-expanded">
              {this.props.options.expandDetailComponent && this.props.options.expandDetailComponent(item)}
            </div>
          )}
        </div>
      );
    });
  };

  /**
   * Prepares pagination for the table. There are two types of pagination:
   *
   *  internal pagination - drives pager component based on the internal state of the component
   *                        (number of results, active page etc.)
   *  external pagination - overrides the internal state, and it is controlled outside
   *                        (pagination can be stored in redux or in other place)
   * */
  createPaginationDom = () => {
    const paginationOverride = this.props.options.paginationOverride;

    const showClientSidePaginationComponents =
      this.props.options.pageable &&
      !this.props.options.paginationOverride &&
      this.props.data?.length !== 0 &&
      this.props.data?.length > this.state.pageSize;

    const showBackEndPaginationComponents =
      this.props.options.pageable && paginationOverride && paginationOverride.total > paginationOverride.pageSize;

    return (
      <>
        {/* Client side pagination, pages are calculated */}
        {!paginationOverride && this.props.data?.length !== 0 && (
          <div className="mt-3 mb-3">
            <BlazePagination
              pageSize={this.state.pageSize}
              showPageSizeDropdown={this.props.showPageSizeDropdown}
              showNavComponents={showClientSidePaginationComponents}
              total={this.props.data?.length}
              activePage={this.state.activePage}
              pageSizeSelectedHandler={this.pageSizeSelectedHandler}
              pageSelectedHandler={this.pageSelectedHandler}
              pageSizeOptions={this.props.pageSizeOptions ? this.props.pageSizeOptions : [5, 10, 25, 50]}
              paginationClassName={this.props.paginationClassName}
            />
          </div>
        )}
        {/* Backand's pagination, pagination is just displayed with provided values and handlers */}
        {paginationOverride && _.size(this.props.data) !== 0 && this.props.options.hidePager !== true && (
          <BlazePagination
            className="pl-3 pr-3"
            pageSize={paginationOverride.pageSize}
            showPageSizeDropdown={this.props.showPageSizeDropdown}
            showNavComponents={showBackEndPaginationComponents}
            total={paginationOverride.total}
            activePage={paginationOverride.activePage}
            pageSizeSelectedHandler={this.pageSizeSelectedHandler}
            pageSelectedHandler={this.pageSelectedHandler}
            pageSizeOptions={this.props.pageSizeOptions ? this.props.pageSizeOptions : [5, 10, 25, 50]}
          />
        )}
      </>
    );
  };

  /**
   * If there is no sorting override defined, the local state sorting is used.
   */
  getSortingConfig = (): SortConfiguration => {
    if (this._isBackendSortingEnabled()) {
      return this.props.options.sortingOverride;
    } else {
      return this.state.sorting;
    }
  };

  updateSortingHandler = (sort: SortConfiguration) => {
    const isBackendSorting = this._isBackendSortingEnabled();
    if (isBackendSorting) {
      this.props.onSort(sort);
    } else {
      this.setState({ sorting: sort });
    }
  };

  createTableBodyDom = () => {
    let message: string;
    if (this.props.options.searchTerm === '') {
      message = this.props.options.defaultEmpty ? this.props.options.defaultEmpty : '';
    } else {
      message = this.props.options.noResultsText ? this.props.options.noResultsText : '';
    }

    let tableBody;
    // Show data, if available
    if (_.size(this.props.data) !== 0) {
      tableBody = this.buildTableItems();
    } else {
      tableBody = (
        <div
          className={clsx(
            'text-center p-5',
            this.props.options.customRowClassName ? this.props.options.customRowClassName : undefined,
          )}
        >
          {message}
        </div>
      );
    }
    return tableBody;
  };

  createTableHeaderDom = () => {
    const header = (
      <BlazeTableHeader
        selectedItemsCount={this.state.selected.length}
        blazeTableColDef={this.props.options.columnDef}
        isSelectable={this.props.options.selectable}
        showRowExpander={this.props.options.showRowExpander}
        showSelectAll={this.getSelectType() === 'checkbox'}
        expandColumnSize={this.expandColumnSize}
        selectColumnSize={this.selectColumnSize}
        selectAllType={this.state.selectAll}
        onSelectAll={this.selectAll}
        onDeselectAll={this.deSelectAll}
        disabled={this.props.options.disabled || false}
        selectAllDropdown={this.props.options.selectAllDropdown}
        sorting={this.getSortingConfig()}
        onChangeSorting={this.updateSortingHandler}
        isSortable={this.props.options.sortable}
      />
    );

    const headerClass = this.props.options.customHeaderClassName
      ? `${this.props.options.customHeaderClassName}`
      : undefined;

    const headerContent = (
      <div className={clsx(headerClass ? headerClass : undefined, 'flex-table-item-top flex-table-item')}>{header}</div>
    );

    // If sticky headers feature is enabled, use sticky wrapper for header
    if (this.props.stickyHeaders === true) {
      return (
        <Sticky>
          {({ style }) => (
            <div className="sticky-header" style={{ ...style, zIndex: 10000 }}>
              {headerContent}
            </div>
          )}
        </Sticky>
      );
    } else {
      return headerContent;
    }
  };

  render() {
    const tableBody = this.createTableBodyDom();

    const tableBodyStyle: CSSProperties = {};
    if (this.props.options.minHeight) {
      tableBodyStyle.minHeight = this.props.options.minHeight;
    }

    if (this.props.options.maxContentHeight) {
      tableBodyStyle.height = this.props.options.maxContentHeight;
      tableBodyStyle.overflow = 'auto';
    }

    const tableStyle: CSSProperties = {};
    if (this.props.options.minWidth) {
      tableStyle.minWidth = this.props.options.minWidth;
    }

    return (
      <StickyContainer>
        <div className={this.props.className}>
          <div style={this.props.options.minWidth ? { width: '100%', overflow: 'auto' } : {}}>
            <div
              className={clsx(
                'flex-table flex-table-xs',
                !_.isNil(this.props.options.customTableClassName) ? this.props.options.customTableClassName : '',
              )}
              id={this.state.tableName}
              style={tableStyle}
            >
              {/* Table Header*/}
              {this.createTableHeaderDom()}
              {/* ./Table Header*/}

              {/* Table Body */}
              <div style={{ position: 'relative' }} className="mb-4">
                {this.props.isLoading && (
                  <div className="blur-background">
                    <FullPageLoader loadingText={this.props.options.loadingText} />
                  </div>
                )}
                <div className={this.props.isLoading ? 'blur' : ''} style={tableBodyStyle}>
                  {tableBody}
                </div>
              </div>
              {/* ./Table Body */}

              {/* Pagination */}
              {this.createPaginationDom()}
              {/* ./Pagination */}
            </div>
          </div>
        </div>
      </StickyContainer>
    );
  }
}

export default BlazeTable;
